import { useCallback, useId, useState } from "react";

interface DisclosureTriggerProps {
  "aria-expanded": boolean;
  "aria-controls": string;
  onClick: () => void;
}

interface DisclosurePanelProps {
  id: string;
}

interface UseDisclosure {
  /** Whether the panel is expanded. */
  open: boolean;
  /** Toggle the panel open/closed. */
  toggle: () => void;
  /** Spread onto the trigger <button>; wires `aria-expanded`, `aria-controls` and `onClick`. */
  triggerProps: DisclosureTriggerProps;
  /** Spread onto the panel container; wires the `id` that `aria-controls` points at. */
  panelProps: DisclosurePanelProps;
}

/**
 * Headless toggle logic for an inline collapsible disclosure. Closed by default.
 *
 * It owns the open state, a stable panel id and the ARIA wiring, but not DOM
 * placement: the caller renders the trigger and the panel wherever it likes.
 * That lets a trigger sit inside a <label> while its panel renders as a
 * sibling outside it, so clicking the panel never toggles the labelled control.
 */
export function useDisclosure(initialOpen = false): UseDisclosure {
  const [open, setOpen] = useState(initialOpen);
  const panelId = useId();
  const toggle = useCallback(() => setOpen((prev) => !prev), []);
  return {
    open,
    toggle,
    triggerProps: {
      "aria-expanded": open,
      "aria-controls": panelId,
      onClick: toggle,
    },
    panelProps: { id: panelId },
  };
}
